import { Fragment, useEffect, useState } from "react";
import {
  Field,
  Label,
  Switch,
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from "@headlessui/react";
import { CheckIcon, MinusIcon, SparklesIcon } from "@heroicons/react/20/solid";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { track } from "../analytics";

const tiers = [
  {
    name: "Starter",
    id: "tier-starter",
    href: "/signup?plan=Starter",
    priceMonthly: "Free",
    description: "Limited version with basic features.",
    mostPopular: false,
    features: {
      crossListing: "Only 1 platform",
      supportedPlatforms: "1 platform",
      emailInventoryTracking: "Limited",
      inAppMessages: "Not available",
      inventoryManagementTools: true,
      aiInsights: "Add-on",
      exportFormats: "Basic (.txt, .csv)",
      scheduledAutoListingSupport: false,
      teamSupport: "Add-on",
      additionalPlatforms: "Add-on",
      prioritySupport: "Add-on",
      customFeatures: false,
      totalInventoryItems: "unlimited",
      saleDetection: "100 items/month",
      hiResImageUpload: "5 images/listing",
      marketPlaceInventoryImport: "Add-on",
      bulkListing: "2 items",
      totalListingsPerMonth: "50 items",
      inventoryAdditionsPerMonth: "100 items",
      customTitles: false,
      draftListing: true,
      backgroundRemoval: "100 items",
      apiAccess: "Add-on",
      accountingSoftwareIntegration: "Add-on",
    },
  },
  {
    name: "Basic",
    id: "tier-basic",
    href: "/signup?plan=Basic",
    priceMonthly: 29.99, // Replace with your desired price
    description: "Core features with expanded capabilities.",
    mostPopular: false,
    features: {
      crossListing: "Up to 3 platforms",
      supportedPlatforms: "3 platforms",
      emailInventoryTracking: "Inventory updates only",
      inAppMessages: "Messages from platforms",
      inventoryManagementTools: true,
      aiInsights: "Add-on",
      exportFormats: "Basic (.txt, .csv, .xlsx)",
      scheduledAutoListingSupport: true,
      teamSupport: "Add-on",
      additionalPlatforms: "Add-on",
      prioritySupport: "Add-on",
      customFeatures: false,
      totalInventoryItems: "Unlimited",
      saleDetection: "1000 items/month",
      bulkListing: "10 items",
      totalListingsPerMonth: "500 items",
      inventoryAdditionsPerMonth: "Unlimited items",
      customTitles: true,
      draftListing: true,
      hiResImageUpload: "15 images/listing",
      marketPlaceInventoryImport: "Add-on",
      backgroundRemoval: "1000 items/month",
      apiAccess: "Add-on",
      accountingSoftwareIntegration: "Add-on",
    },
  },
  {
    name: "Premium",
    id: "tier-premium",
    href: "/signup?plan=Premium",
    priceMonthly: 69.99, // Replace with your desired price
    description: "Full access to advanced features and integrations.",
    mostPopular: true,
    features: {
      crossListing: "Up to 5 platforms",
      supportedPlatforms: "Up to 5 platforms",
      emailInventoryTracking: "Full access",
      inAppMessages: "Messages, notifications & alerts",
      inventoryManagementTools: true,
      aiInsights: true,
      exportFormats: "All formats",
      scheduledAutoListingSupport: true,
      teamSupport: "Add-on",
      additionalPlatforms: "Included",
      prioritySupport: "Add-on",
      customFeatures: false,
      totalInventoryItems: "Unlimited",
      saleDetection: "All listed items",
      bulkListing: "100 items",
      totalListingsPerMonth: "2000 items",
      inventoryAdditionsPerMonth: "Unlimited items",
      customTitles: true,
      draftListing: true,
      backgroundRemoval: "Unlimited",
      hiResImageUpload: "25 images/listing",
      marketPlaceInventoryImport: "Included",
      apiAccess: "Included",
      accountingSoftwareIntegration: "Included",
    },
  },
  {
    name: "Enterprise",
    id: "tier-enterprise",
    href: "/signup?plan=Enterprise",
    priceMonthly: "Contact Us",
    description: "Customized solutions with unlimited features.",
    mostPopular: false,
    features: {
      crossListing: "Unlimited platforms",
      supportedPlatforms: "Unlimited platforms",
      emailInventoryTracking: "Full access",
      inAppMessages: "Messages and responses",
      inventoryManagementTools: true,
      aiInsights: true,
      exportFormats: "All formats",
      scheduledAutoListingSupport: true,
      teamSupport: "Included",
      additionalPlatforms: "Included",
      prioritySupport: "Included",
      customFeatures: true,
      totalInventoryItems: "Unlimited",
      saleDetection: "All listed items",
      bulkListing: "Unlimited",
      totalListingsPerMonth: "Unlimited",
      inventoryAdditionsPerMonth: "Unlimited items",
      customTitles: true,
      draftListing: true,
      hiResImageUpload: "25 images/listing",
      marketPlaceInventoryImport: "Included",
      backgroundRemoval: "Unlimited",
      apiAccess: "Included",
      accountingSoftwareIntegration: "Included",
    },
  },
];

const sections = [
  {
    name: "Features",
    features: [
      {
        name: "Cross Listing",
        tiers: {
          Starter: "Only 1 platform",
          Basic: "Up to 3 platforms",
          Premium: "Up to 5 platforms",
          Enterprise: "Unlimited platforms",
        },
      },
      {
        name: "Supported Platforms",
        tiers: {
          Starter: "Only 1 platform",
          Basic: "Up to 3 platforms",
          Premium: "Up to 5 platforms",
          Enterprise: "Unlimited platforms",
        },
      },
      {
        name: "Email/Inventory Tracking",
        tiers: {
          Starter: false,
          Basic: "Inventory updates only",
          Premium: "Full access",
          Enterprise: "Full access",
        },
      },
      {
        name: "In-App Messages",
        tiers: {
          Starter: "Not available",
          Basic: "Messages from platforms",
          Premium: "Messages, Notifications & Alerts",
          Enterprise: "Messages, Notifications & Alerts",
        },
      },
      {
        name: "Inventory Management Tools",
        tiers: { Starter: true, Basic: true, Premium: true, Enterprise: true },
      },
      {
        name: "AI Insights",
        tiers: {
          Starter: "Add-on",
          Basic: "Add-on",
          Premium: true,
          Enterprise: true,
        },
      },
      {
        name: "Export Formats",
        tiers: {
          Starter: "Basic (.txt, .csv)",
          Basic: "Basic (.txt, .csv, .xlsx)",
          Premium: "All formats",
          Enterprise: "All formats",
        },
      },
      {
        name: "Scheduled and Auto Listing Support",
        tiers: { Starter: false, Basic: true, Premium: true, Enterprise: true },
      },
      {
        name: "Team Support",
        tiers: {
          Starter: "Add-on",
          Basic: "Add-on",
          Premium: "Add-on",
          Enterprise: "Included",
        },
      },
      {
        name: "Total Inventory Items",
        tiers: {
          Starter: "100 Items",
          Basic: "Unlimited",
          Premium: "Unlimited",
          Enterprise: "Unlimited",
        },
      },
      {
        name: "Auto Sale Detection",
        tiers: {
          Starter: "100 items/month",
          Basic: "1000 items/month",
          Premium: "All listed items",
          Enterprise: "All listed items",
        },
      },
      {
        name: "Bulk Listing",
        tiers: {
          Starter: "2 items",
          Basic: "10 items",
          Premium: "100 items",
          Enterprise: "Unlimited Items",
        },
      },
      {
        name: "Total listings per Month",
        tiers: {
          Starter: "10 items",
          Basic: "500 items",
          Premium: "2000 items",
          Enterprise: "Unlimited Items",
        },
      },
      {
        name: "Inventory additions per Month",
        tiers: {
          Starter: "10 items",
          Basic: "Unlimited Items",
          Premium: "Unlimited Items",
          Enterprise: "Unlimited Items",
        },
      },
      {
        name: "Custom Titles/Templates per Marketplace",
        tiers: { Starter: false, Basic: true, Premium: true, Enterprise: true },
      },
      {
        name: "Draft Listing",
        tiers: { Starter: true, Basic: true, Premium: true, Enterprise: true },
      },
      {
        name: "Background Removal",
        tiers: {
          Starter: "10 items/month",
          Basic: "1000 items/month",
          Premium: "Unlimited",
          Enterprise: "Unlimited",
        },
      },
      {
        name: "High Resolution image upload per listing",
        tiers: {
          Starter: "5 images/listing",
          Basic: "15 images/listing",
          Premium: "25 images/listing",
          Enterprise: "25 images/listing",
        },
      },
      {
        name: "Import Inventory from marketplace listings",
        tiers: {
          Starter: "Add-on",
          Basic: "Add-on",
          Premium: "Included",
          Enterprise: "Included",
        },
      },
      {
        name: "API Access",
        tiers: {
          Starter: "Add-on",
          Basic: "Add-on",
          Premium: "Add-on",
          Enterprise: "Included",
        },
      },
      {
        name: "Accounting Software Integration",
        tiers: {
          Starter: "Add-on",
          Basic: "Add-on",
          Premium: "Add-on",
          Enterprise: "Included",
        },
      },
    ],
  },
  {
    name: "Support",
    features: [
      {
        name: "Customer Service",
        tiers: {
          Starter: "Discord Chat",
          Basic: "Limited help",
          Premium: "Timely help",
          Enterprise: "Dedicated help",
        },
      },
      {
        name: "Priority Support",
        tiers: {
          Starter: "Add-on",
          Basic: "Add-on",
          Premium: "Add-on",
          Enterprise: "Included (24/7)",
        },
      },
      {
        name: "Custom Features",
        tiers: {
          Starter: false,
          Basic: false,
          Premium: false,
          Enterprise: "Contact us for custom requests",
        },
      },
    ],
  },
  {
    name: "Add-Ons",
    features: [
      {
        name: "Additional Platforms",
        tiers: {
          Starter: "$4.99 per platform/month",
          Basic: "$4.99 per platform/month",
          Premium: "Included",
          Enterprise: "Included",
        },
      },
      {
        name: "Priority Support",
        tiers: {
          Starter: "$9.99 / month",
          Basic: "$9.99 / month",
          Premium: "$9.99 / month",
          Enterprise: "Included",
        },
      },
      {
        name: "Team Support",
        tiers: {
          Starter: "$9.99/month per additional member",
          Basic: "$9.99/month per additional member",
          Premium: "$9.99/month per additional member",
          Enterprise: "Included",
        },
      },
      {
        name: "AI Insights",
        tiers: {
          Starter: "$4.99 / month",
          Basic: "$4.99 / month",
          Premium: "Included",
          Enterprise: "Included",
        },
      },
      {
        name: "Marketplace Inventory Import",
        tiers: {
          Starter: "$3.99 / month",
          Basic: "$3.99 / month",
          Premium: "Included",
          Enterprise: "Included",
        },
      },
      {
        name: "Email Tracking",
        tiers: {
          Starter: "$14.99 / month",
          Basic: "$14.99 / month",
          Premium: "Full tracking",
          Enterprise: "Full tracking",
        },
      },
      {
        name: "API Access",
        tiers: {
          Starter: "$14.99 / month",
          Basic: "$14.99 / month",
          Premium: "$14.99 / month",
          Enterprise: "Included",
        },
      },
      {
        name: "Accounting Software Integration",
        tiers: {
          Starter: "$9.99 / month",
          Basic: "$9.99 / month",
          Premium: "$14.99 / month",
          Enterprise: "Included",
        },
      },
    ],
  },
];

function classNames(...classes: any) {
  return classes.filter(Boolean).join(" ");
}

export default function Pricing() {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    track(
      "SITE:PRICING_VIEWED",
      {
        billing_interval: enabled ? "year" : "month",
        page_kind: "pricing",
      },
      {
        routePath: "/pricing",
        eventOwner: "frontend_intent",
        nonInteraction: true,
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    track("SITE:PRICING_BILLING_INTERVAL_CHANGED", {
      billing_interval: enabled ? "year" : "month",
      page_kind: "pricing",
    });
  }, [enabled]);

  const getPriceDisplay = (price: any) => {
    if (typeof price === "string") {
      return price;
    }
    const formattedPrice = enabled
      ? (price * 12 * 0.8).toFixed(2)
      : price.toFixed(2);
    return `$${formattedPrice}`;
  };

  const getFullPriceDisplay = (price: any) => {
    if (typeof price === "string") {
      return "";
    }
    return `$${(price * 12).toFixed(2)}`;
  };

  return (
    <div className="bg-white py-24 sm:py-32 dark:bg-gray-900">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mx-auto max-w-4xl text-center">
          <p className="text-base font-semibold leading-7 bg-gradient-to-r from-softBlue via-brightPurple to-deepPurple bg-clip-text text-transparent">
            Pricing
          </p>
          <h1 className="mt-2 sm:mt-4 text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-5xl">
            TrackNList Pricing — Plans for Every Reseller
          </h1>
        </div>
        <p className="mx-auto mt-2 sm:mt-6 max-w-2xl text-center text-base sm:text-lg leading-7 sm:leading-8 text-gray-600 dark:text-gray-300">
          Choose the plan that best fits your needs. Whether you're just
          starting out or looking to scale, we have a plan for you.
        </p>

        <Field className="flex mt-6 sm:mt-12 items-center space-x-2 sm:space-x-4 place-content-center">
          <Label as="span" className="ml-3 text-sm">
            <span className="font-medium text-gray-900 dark:text-gray-100">
              Monthly
            </span>
          </Label>
          <Switch
            checked={enabled}
            onChange={setEnabled}
            className="group relative inline-flex h-7 w-12 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-gray-200 dark:bg-gray-700 transition-all duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-softBlue focus:ring-offset-2 data-[checked]:bg-gradient-to-r data-[checked]:from-softBlue data-[checked]:via-brightPurple data-[checked]:to-deepPurple shadow-lg"
          >
            <span
              aria-hidden="true"
              className="pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow-lg ring-0 transition-all duration-300 ease-in-out group-data-[checked]:translate-x-5"
            />
          </Switch>
          <Label as="span" className="ml-3 text-sm">
            <span className="font-medium text-gray-900 dark:text-gray-100">
              Annual billing
            </span>{" "}
            <span className="inline-flex items-center rounded-full bg-gradient-to-r from-softBlue/10 via-brightPurple/10 to-deepPurple/10 px-2.5 py-0.5 text-xs font-semibold text-softBlue dark:text-brightPurple ring-1 ring-inset ring-softBlue/20 dark:ring-brightPurple/20">
              Save 20%
            </span>
          </Label>
        </Field>

        {/* xs to lg */}
        <div className="mx-auto mt-6 max-w-md space-y-4 sm:space-y-8 sm:mt-16 lg:hidden">
          {tiers.map((tier) => (
            <section
              key={tier.id}
              className={classNames(
                tier.mostPopular
                  ? "relative rounded-2xl bg-gradient-to-br from-softBlue/5 via-brightPurple/5 to-deepPurple/5 ring-2 ring-inset ring-softBlue/20 dark:ring-brightPurple/20 shadow-xl shadow-softBlue/10 dark:shadow-brightPurple/10"
                  : "relative rounded-2xl bg-white dark:bg-gray-800 ring-1 ring-inset ring-gray-200 dark:ring-gray-700 shadow-lg hover:shadow-xl transition-shadow duration-300",
                "p-5 sm:p-8 overflow-hidden"
              )}
            >
              {tier.mostPopular && (
                <div className="absolute top-0 right-0">
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-br from-softBlue via-brightPurple to-deepPurple opacity-20 blur-2xl" />
                    <div className="relative flex items-center gap-1 bg-gradient-to-r from-softBlue via-brightPurple to-deepPurple px-3 py-1 rounded-bl-2xl rounded-tr-2xl">
                      <SparklesIcon className="h-4 w-4 text-white" />
                      <span className="text-xs font-bold text-white">
                        Most Popular
                      </span>
                    </div>
                  </div>
                </div>
              )}
              <h3
                id={tier.id}
                className={classNames(
                  "text-lg font-bold leading-6",
                  tier.mostPopular
                    ? "bg-gradient-to-r from-softBlue via-brightPurple to-deepPurple bg-clip-text text-transparent"
                    : "text-gray-900 dark:text-white"
                )}
              >
                {tier.name}
              </h3>
              <p className="mt-2 sm:mt-4 flex items-baseline relative gap-x-2 text-gray-900 dark:text-white">
                <span className="text-2xl sm:text-5xl font-bold">
                  {getPriceDisplay(tier.priceMonthly)}
                </span>
                {enabled && typeof tier.priceMonthly === "number" && (
                  <span className="text-base sm:text-lg text-gray-500 dark:text-gray-400 line-through">
                    {getFullPriceDisplay(tier.priceMonthly)}
                  </span>
                )}
                {tier.name.toLowerCase() !== "enterprise" && (
                  <span className="text-xs sm:text-sm font-semibold text-gray-500 dark:text-gray-400">
                    {enabled ? "/year" : "/month"}
                  </span>
                )}
              </p>
              {tier.description && (
                <p className="mt-0 sm:mt-2 text-sm text-gray-600 dark:text-gray-400">
                  {tier.description}
                </p>
              )}
              <Link
                to={`${tier.href}${
                  enabled ? "&interval=year" : "&interval=month"
                }`}
                aria-describedby={tier.id}
                onClick={() =>
                  track("SITE:PLAN_CTA_CLICKED", {
                    selected_plan: tier.name,
                    billing_interval: enabled ? "year" : "month",
                    page_kind: "pricing",
                  })
                }
                className={classNames(
                  tier.mostPopular
                    ? "relative mt-4 sm:mt-8 block rounded-xl bg-gradient-to-r from-softBlue via-brightPurple to-deepPurple px-4 py-3 text-center text-sm font-semibold text-white shadow-lg shadow-softBlue/30 hover:shadow-xl hover:shadow-softBlue/40 transition-all duration-300 hover:scale-105 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-softBlue"
                    : "mt-4 sm:mt-8 block rounded-xl bg-white dark:bg-gray-700 px-4 py-3 text-center text-sm font-semibold text-softBlue dark:text-brightPurple ring-2 ring-inset ring-softBlue/20 dark:ring-brightPurple/20 hover:ring-softBlue/40 dark:hover:ring-brightPurple/40 hover:bg-softBlue/5 dark:hover:bg-brightPurple/5 transition-all duration-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-softBlue",
                  "transform"
                )}
              >
                {tier.name.toLowerCase() === "enterprise"
                  ? "Contact Us"
                  : "Buy plan"}
              </Link>
              <ul className="mt-4 sm:mt-10 space-y-1 sm:space-y-6 text-sm leading-6 text-gray-900 dark:text-white">
                {sections.map((section) => (
                  <li key={section.name}>
                    {/* Desktop: Always visible */}
                    <div className="hidden sm:block">
                      <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2 sm:mb-4">
                        {section.name}
                      </h4>
                      <ul className="space-y-2 sm:space-y-3">
                        {section.features.map((feature: any) =>
                          feature.tiers[tier.name] ? (
                            <li
                              key={feature.name}
                              className="flex gap-x-3 items-start"
                            >
                              <CheckIcon
                                aria-hidden="true"
                                className={classNames(
                                  "h-5 w-5 flex-none mt-0.5",
                                  tier.mostPopular
                                    ? "text-softBlue"
                                    : "text-indigo-600 dark:text-brightPurple"
                                )}
                              />
                              <span className="flex-1">
                                <span className="font-medium">
                                  {feature.name}
                                </span>
                                {typeof feature.tiers[tier.name] ===
                                "string" ? (
                                  <span className="block text-xs leading-5 text-gray-500 dark:text-gray-400 mt-0.5">
                                    {feature.tiers[tier.name]}
                                  </span>
                                ) : null}
                              </span>
                            </li>
                          ) : null
                        )}
                      </ul>
                    </div>

                    {/* Mobile: Collapsible accordion */}
                    <Disclosure
                      defaultOpen={false}
                      as="div"
                      className="sm:hidden"
                    >
                      {({ open }) => (
                        <>
                          <DisclosureButton className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                            <span>{section.name}</span>
                            <motion.div
                              animate={{ rotate: open ? 180 : 0 }}
                              transition={{ duration: 0.2 }}
                            >
                              <ChevronDownIcon className="h-4 w-4" />
                            </motion.div>
                          </DisclosureButton>
                          <DisclosurePanel static>
                            <motion.ul
                              initial={{ opacity: 0, height: 0 }}
                              animate={{
                                opacity: open ? 1 : 0,
                                height: open ? "auto" : 0,
                              }}
                              transition={{ duration: 0.3, ease: "easeInOut" }}
                              className="space-y-2 sm:space-y-3 mt-3 overflow-hidden"
                            >
                              {section.features.map(
                                (feature: any, featureIndex: number) =>
                                  feature.tiers[tier.name] ? (
                                    <motion.li
                                      key={feature.name}
                                      initial={{ opacity: 0, x: -10 }}
                                      animate={{
                                        opacity: open ? 1 : 0,
                                        x: open ? 0 : -10,
                                      }}
                                      transition={{
                                        duration: 0.2,
                                        delay: open ? featureIndex * 0.03 : 0,
                                      }}
                                      className="flex gap-x-3 items-start"
                                    >
                                      <CheckIcon
                                        aria-hidden="true"
                                        className={classNames(
                                          "h-5 w-5 flex-none mt-0.5",
                                          tier.mostPopular
                                            ? "text-softBlue"
                                            : "text-indigo-600 dark:text-brightPurple"
                                        )}
                                      />
                                      <span className="flex-1">
                                        <span className="font-medium">
                                          {feature.name}
                                        </span>
                                        {typeof feature.tiers[tier.name] ===
                                        "string" ? (
                                          <span className="block text-xs leading-5 text-gray-500 dark:text-gray-400 mt-0.5">
                                            {feature.tiers[tier.name]}
                                          </span>
                                        ) : null}
                                      </span>
                                    </motion.li>
                                  ) : null
                              )}
                            </motion.ul>
                          </DisclosurePanel>
                        </>
                      )}
                    </Disclosure>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>

        {/* lg+ */}
        <div className="isolate mt-20 hidden lg:block">
          <div className="relative -mx-8">
            {tiers.some((tier) => tier.mostPopular) ? (
              <div className="absolute inset-x-4 inset-y-0 -z-10 flex">
                <div
                  style={{
                    marginLeft: `${
                      (tiers.findIndex((tier) => tier.mostPopular) + 1) * 20
                    }%`,
                  }}
                  aria-hidden="true"
                  className="flex w-1/5 px-4"
                >
                  <div className="w-full rounded-t-2xl border-x-2 border-t-2 border-softBlue/30 dark:border-brightPurple/30 bg-gradient-to-b from-softBlue/5 via-brightPurple/5 to-deepPurple/5 shadow-lg" />
                </div>
              </div>
            ) : null}
            <table className="w-full table-fixed border-separate border-spacing-x-8 text-left">
              <caption className="sr-only">Pricing plan comparison</caption>
              <colgroup>
                <col className="w-1/5" />
                <col className="w-1/5" />
                <col className="w-1/5" />
                <col className="w-1/5" />
                <col className="w-1/5" />
              </colgroup>
              <thead>
                <tr>
                  <td />
                  {tiers.map((tier) => (
                    <th
                      key={tier.id}
                      scope="col"
                      className="px-6 pt-6 xl:px-8 xl:pt-8"
                    >
                      <div className="relative">
                        <div
                          className={classNames(
                            "text-lg font-bold text-center leading-7",
                            tier.mostPopular
                              ? "bg-gradient-to-r from-softBlue via-brightPurple to-deepPurple bg-clip-text text-transparent"
                              : "text-gray-900 dark:text-white"
                          )}
                        >
                          {tier.name}
                        </div>
                        {tier.mostPopular && (
                          <div className="absolute -top-3 left-1/2 -translate-x-1/2 flex items-center gap-1">
                            <SparklesIcon className="h-4 w-4 text-softBlue" />
                            <span className="text-xs font-semibold bg-gradient-to-r from-softBlue via-brightPurple to-deepPurple bg-clip-text text-transparent">
                              Popular
                            </span>
                          </div>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <th scope="row">
                    <span className="sr-only">Price</span>
                  </th>
                  {tiers.map((tier, index) => (
                    <td key={tier.id} className="px-6 text-center pt-4 xl:px-8">
                      <div className="flex relative justify-center items-baseline text-center gap-x-2 text-gray-900 dark:text-white">
                        {enabled && typeof tier.priceMonthly === "number" && (
                          <span className="absolute -top-4 left-1/2 -translate-x-1/2 text-sm text-gray-500 dark:text-gray-400 line-through whitespace-nowrap">
                            {getFullPriceDisplay(tier.priceMonthly)}
                          </span>
                        )}
                        <span className="text-2xl text-center font-bold">
                          {getPriceDisplay(tier.priceMonthly)}
                        </span>
                        {tier.name.toLowerCase() !== "enterprise" && (
                          <span className="text-sm text-gray-500 dark:text-gray-400 font-semibold leading-6">
                            {enabled ? "/year" : "/month"}
                          </span>
                        )}
                      </div>
                      {tier.description && (
                        <p className="mt-2 text-xs text-gray-600 dark:text-gray-400 text-center">
                          {tier.description}
                        </p>
                      )}
                      <Link
                        to={`${tier.href}${
                          enabled ? "&interval=year" : "&interval=month"
                        }`}
                        onClick={() =>
                          track("SITE:PLAN_CTA_CLICKED", {
                            selected_plan: tier.name,
                            billing_interval: enabled ? "year" : "month",
                            page_kind: "pricing",
                          })
                        }
                        className={classNames(
                          tier.mostPopular
                            ? "relative mt-6 block rounded-xl bg-gradient-to-r from-softBlue via-brightPurple to-deepPurple px-4 py-2.5 text-center text-sm font-semibold text-white shadow-lg shadow-softBlue/30 hover:shadow-xl hover:shadow-softBlue/40 transition-all duration-300 hover:scale-105 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-softBlue"
                            : "mt-6 block rounded-xl bg-white dark:bg-gray-700 px-4 py-2.5 text-center text-sm font-semibold text-softBlue dark:text-brightPurple ring-2 ring-inset ring-softBlue/20 dark:ring-brightPurple/20 hover:ring-softBlue/40 dark:hover:ring-brightPurple/40 hover:bg-softBlue/5 dark:hover:bg-brightPurple/5 transition-all duration-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-softBlue",
                          "transform"
                        )}
                      >
                        {tier.name.toLowerCase() === "enterprise"
                          ? "Contact Us"
                          : "Buy plan"}
                      </Link>
                    </td>
                  ))}
                </tr>
                {sections.map((section, sectionIdx) => (
                  <Fragment key={section.name}>
                    <tr>
                      <th
                        scope="colgroup"
                        colSpan={5}
                        className={classNames(
                          sectionIdx === 0 ? "pt-8" : "pt-16",
                          "pb-6 text-base font-bold leading-6 text-gray-900 dark:text-white"
                        )}
                      >
                        <div className="flex items-center gap-3">
                          <div className="h-px flex-1 bg-gradient-to-r from-transparent via-softBlue/30 to-transparent" />
                          <span className="bg-gradient-to-r from-softBlue via-brightPurple to-deepPurple bg-clip-text text-transparent">
                            {section.name}
                          </span>
                          <div className="h-px flex-1 bg-gradient-to-r from-transparent via-softBlue/30 to-transparent" />
                        </div>
                      </th>
                    </tr>
                    {section.features.map((feature: any) => (
                      <tr key={feature.name}>
                        <th
                          scope="row"
                          className="py-4 text-sm font-medium leading-6 text-gray-900 dark:text-white group"
                        >
                          <span className="group-hover:text-softBlue dark:group-hover:text-brightPurple transition-colors duration-200">
                            {feature.name}
                          </span>
                        </th>
                        {tiers.map((tier: any) => (
                          <td
                            key={tier.id}
                            className={classNames(
                              "px-6 py-4 xl:px-8 transition-colors duration-200",
                              tier.mostPopular &&
                                "bg-gradient-to-b from-softBlue/5 via-brightPurple/5 to-deepPurple/5"
                            )}
                          >
                            {typeof feature.tiers[tier.name] === "string" ? (
                              <div className="text-center text-sm leading-6 text-gray-600 dark:text-gray-300 font-medium">
                                {feature.tiers[tier.name]}
                              </div>
                            ) : (
                              <>
                                {feature.tiers[tier.name] === true ? (
                                  <div className="flex justify-center">
                                    <div className="rounded-full bg-gradient-to-br from-softBlue/20 to-brightPurple/20 p-1">
                                      <CheckIcon
                                        aria-hidden="true"
                                        className="h-5 w-5 text-softBlue dark:text-brightPurple"
                                      />
                                    </div>
                                  </div>
                                ) : (
                                  <div className="flex justify-center">
                                    <MinusIcon
                                      aria-hidden="true"
                                      className="h-5 w-5 text-gray-300 dark:text-gray-600"
                                    />
                                  </div>
                                )}

                                <span className="sr-only">
                                  {feature.tiers[tier.name] === true
                                    ? "Included"
                                    : "Not included"}{" "}
                                  in {tier.name}
                                </span>
                              </>
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
